import React, { useCallback } from 'react'
import {
  Toolbar,
  ToolbarButton,
  ToolbarLayout,
  ToolbarRail,
} from '@workwave-tidal/lab/Toolbar'
import { CustomToolbarLogo } from './Toolbar/CustomToolbarLogo'
import { WorkWave } from '@workwave-tidal/core/icons/logos'
import { ArrowBack } from '@workwave-tidal/core/icons/ArrowBack'
import { Search } from '@workwave-tidal/core/icons/Search'
import { FilterAlt } from '@workwave-tidal/core/icons/FilterAlt'
import { Transition } from '@user/components/FullScreenDialog/Transition'
import Dialog from '@mui/material/Dialog'
import Box from '@mui/material/Box'
import AppBar from '@mui/material/AppBar'
import Button from '@mui/material/Button'
import Badge from '@mui/material/Badge'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { useSelector } from 'react-redux'
import { selectSearchAreaBoundary } from '@user/store/map'

type Props = {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  onChangeViews: (type: string) => void
}

export function MapViewFullScreenDialog({
  open,
  onClose,
  children,
  onChangeViews,
}: Props) {
  const isMobile = useMediaQuery('(max-width: 600px)')

  const tidalThemeActive = useTidalThemeActive()

  const searchAreaBoundary = useSelector(selectSearchAreaBoundary)
  const showBoundaryBadge = Boolean(searchAreaBoundary)

  const searchIconWithBadge = (
    <Badge
      variant="dot"
      invisible={!showBoundaryBadge}
      overlap="circular"
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{
        '& .MuiBadge-dot': {
          backgroundColor: '#E12C2C',
          border: '1.5px solid #FFFFFF',
          height: 10,
          minWidth: 10,
          borderRadius: '50%',
        },
      }}
    >
      <Search />
    </Badge>
  )

  const handleChangeViews = useCallback(
    (viewName: string[]) => {
      switch (viewName[0]) {
        case 'back':
          onChangeViews('back')
          onClose()
          break
        case 'search':
        case 'filter':
          onChangeViews(viewName[0])
          break
      }
    },
    [onChangeViews, onClose],
  )

  // Toolbar width for desktop layout
  const toolbarWidth = 72

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <Box sx={{ height: '100svh', width: '100%', position: 'relative' }}>
        {/* Mobile AppBar - hidden on desktop */}
        <AppBar
          sx={{
            backgroundColor: '#002D5C',
            height: '64px',
            m: 0,
            p: 0,
            display: isMobile ? 'flex' : 'none',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              paddingLeft: 1.2,
              paddingRight: 1.2,
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1.2,
              }}
            >
              <Box py={1.5}>
                <Box
                  sx={{
                    height: 40,
                    width: 40,
                  }}
                >
                  <WorkWave variant="icon" mode="contrast" />
                </Box>
              </Box>
              <Button
                onClick={() => handleChangeViews(['back'])}
                sx={{
                  color: 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <ArrowBack />
                <Typography fontSize={'11px'}>Back</Typography>
              </Button>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Button
                onClick={() => handleChangeViews(['filter'])}
                sx={{
                  color: 'white',
                  display: 'flex',
                  height: '100%',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <FilterAlt />
                <Typography fontSize={'11px'}>Filter</Typography>
              </Button>
              <Button
                onClick={() => handleChangeViews(['search'])}
                sx={{
                  color: 'white',
                  display: 'flex',
                  height: '100%',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {searchIconWithBadge}
              </Button>
            </Box>
          </Box>
        </AppBar>

        {/* Desktop Toolbar - hidden on mobile */}
        <Box
          sx={{
            display: isMobile ? 'none' : 'block',
            position: 'fixed',
            left: 0,
            top: 0,
            height: '100%',
            zIndex: 1301,
          }}
        >
          <Toolbar onChangeViews={handleChangeViews} views={[]}>
            <ToolbarLayout>
              <ToolbarRail
                id="mapViewToolbarContainer"
                sx={{
                  zIndex: 1301,
                  backgroundColor: '#002D5C',
                  height: '100svh',
                  gap: tidalThemeActive ? undefined : 0,
                }}
              >
                <CustomToolbarLogo
                  logo={<WorkWave variant="icon" mode="contrast" />}
                  activeLogo={<WorkWave variant="icon" mode="contrast" />}
                />
                <ToolbarButton
                  icon={<ArrowBack />}
                  selectedIcon={<ArrowBack />}
                  showLabel
                  value={'back'}
                  label={'Back'}
                  sx={{
                    mx: tidalThemeActive ? undefined : '1px',
                    gap: tidalThemeActive ? undefined : 0.2,
                    mt: tidalThemeActive ? undefined : 0,
                    '& .icon': {
                      width: tidalThemeActive ? undefined : '48px',
                      height: tidalThemeActive ? undefined : '32px',
                    },
                  }}
                />

                <ToolbarButton
                  icon={searchIconWithBadge}
                  selectedIcon={searchIconWithBadge}
                  showLabel
                  value={'search'}
                  label={'Search'}
                  sx={{
                    mx: tidalThemeActive ? undefined : '1px',
                    gap: tidalThemeActive ? undefined : 0.2,
                    mt: tidalThemeActive ? undefined : 0,
                    '& .icon': {
                      width: tidalThemeActive ? undefined : '48px',
                      height: tidalThemeActive ? undefined : '32px',
                    },
                  }}
                />

                <ToolbarButton
                  icon={<FilterAlt />}
                  selectedIcon={<FilterAlt />}
                  showLabel
                  value={'filter'}
                  label={'Filter'}
                  sx={{
                    mx: tidalThemeActive ? undefined : '1px',
                    gap: tidalThemeActive ? undefined : 0.2,
                    mt: tidalThemeActive ? undefined : 0,
                    '& .icon': {
                      width: tidalThemeActive ? undefined : '48px',
                      height: tidalThemeActive ? undefined : '32px',
                    },
                  }}
                />
              </ToolbarRail>
            </ToolbarLayout>
          </Toolbar>
        </Box>

        {/* Children - always rendered in the same position, adjusts layout based on viewport */}
        <Box
          sx={{
            height: isMobile ? 'calc(100svh - 64px)' : '100%',
            width: isMobile ? '100%' : `calc(100% - ${toolbarWidth}px)`,
            marginLeft: isMobile ? 0 : `${toolbarWidth}px`,
          }}
        >
          {children}
        </Box>
      </Box>
    </Dialog>
  )
}
